import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  ShieldCheck,
  Bell,
  FileText,
  Layers,
  Box,
  ChevronDown,
  HardDrive,
  Share2,
  Eye,
  EyeOff,
  Link2,
  ExternalLink,
  Save,
  Cloud,
} from 'lucide-react';

const tabs = [
  'Overview',
  'Anchored Records',
  'IPFS Files',
  'Verifications',
  'Nodes',
  'Smart Contracts',
  'Settings',
];

const TAB_ROUTES = {
  overview: '/blockchain-anchoring',
  'anchored-records': '/blockchain-anchoring/anchored-records',
  'ipfs-files': '/blockchain-anchoring/ipfs-files',
  verifications: '/blockchain-anchoring/verifications',
  nodes: '/blockchain-anchoring/nodes',
  'smart-contracts': '/blockchain-anchoring/smart-contracts',
  settings: '/blockchain-anchoring/settings',
};

const settingsNavItems = [
  { name: 'General', icon: SettingsIcon, section: 'general' },
  { name: 'Blockchain Network', icon: Layers, section: 'network' },
  { name: 'IPFS Configuration', icon: Box, section: 'ipfs' },
  { name: 'Security', icon: ShieldCheck, section: 'security' },
  { name: 'Notifications', icon: Bell, section: 'notifications' },
  { name: 'Audit & Logs', icon: FileText, section: 'audit' },
  { name: 'Integration', icon: Share2, section: 'integration' },
  { name: 'Backup & Recovery', icon: HardDrive, section: 'backup' },
];

const INITIAL_FORM_DATA = {
  orgName: 'Intrust Innovation Labs',
  appName: 'I-PQSign',
  timezone: 'Asia/Kolkata (UTC+05:30)',
  defaultLanguage: 'English',
  networkType: 'Ethereum (Mainnet)',
  rpcEndpoint: 'https://mainnet.infura.io/v3/xxxxxxwx',
  chainId: '1',
  blockExplorer: 'https://etherscan.io',
  ipfsNodeType: 'Public Gateway',
  gatewayUrl: 'https://ipfs.io/ipfs/',
  pinningService: 'Pinata',

  // Empty values display a masked placeholder, not fake saved credentials.
  apiKey: '',
  secretKey: '',

  e2eEncryption: true,
  hsm: true,
  multiSig: false,
  certPinning: true,
  keyRotation: 'Every 90 days',
  emailNotifications: true,
  smsNotifications: false,
  webhookUrl: 'https://yourapp.com/webhook',
  notificationEvents: 'All Events',
  logLevel: 'Info',
  retentionPeriod: '12 Months',
  siemIntegration: 'Splunk',
  monitoring: 'Prometheus',
  apiAccess: true,
  autoBackup: true,
  backupFrequency: 'Daily',
  rpo: '1 Hour',
};

const styles = `
  .blockchain-settings {
    --accent: #4c0bfa;
    --text: #20385d;
    --muted: #6480a5;
    --border: #e5edf7;
    --field-height: clamp(19px, 2.2vw, 29px);
    --label-size: clamp(7px, .77vw, 11px);
    --body-size: clamp(7px, .8vw, 11px);
    --title-size: clamp(8px, .9vw, 13px);
    --description-size: clamp(6.5px, .7vw, 10px);
    --card-padding: clamp(11px, 1.35vw, 19px);
    --gap: clamp(8px, 1vw, 14px);

    min-height: 100vh;
    color: var(--text);
    background: white;
    padding: 0;
  }

  .settings-shell {
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0;
  }

  .settings-layout {
    display: grid;
    grid-template-columns: clamp(130px, 15.3vw, 215px) minmax(0, 1fr);
    align-items: stretch;
    gap: var(--gap);
  }

  .settings-sidebar,
  .settings-card {
    border: 1px solid var(--border);
    border-radius: 4px;
    background: rgba(255, 255, 255, .91);
    box-shadow: 0 2px 9px rgba(64, 108, 163, .025);
  }

  .settings-sidebar {
    padding: clamp(6px, .7vw, 10px);
  }

  .settings-nav-button {
    display: flex;
    width: 100%;
    align-items: center;
    gap: clamp(9px, 1vw, 14px);
    min-height: clamp(25px, 3.5vw, 45px);
    margin-bottom: 4px;
    padding: 6px clamp(7px, .8vw, 12px);
    border: 0;
    border-radius: 4px;
    color: #42618c;
    background: transparent;
    font-size: var(--body-size);
    font-weight: 500;
    text-align: left;
    cursor: pointer;
    transition: background .15s, color .15s;
  }

  .settings-nav-button:hover {
    background: #f5f7ff;
  }

  .settings-nav-button.active {
    color: var(--accent);
    background: #f0edff;
  }

  .settings-nav-icon {
    width: clamp(12px, 1.35vw, 18px);
    height: clamp(12px, 1.35vw, 18px);
    flex-shrink: 0;
    color: #24539b;
  }

  .settings-nav-button.active .settings-nav-icon {
    color: var(--accent);
  }

  .settings-content {
    min-width: 0;
  }

  .settings-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-template-rows:
      minmax(clamp(202px, 23.7vw, 325px), auto)
      minmax(clamp(230px, 27vw, 365px), auto)
      minmax(clamp(108px, 12.7vw, 175px), auto);
    gap: var(--gap);
    align-items: stretch;
  }

  .settings-card {
    min-width: 0;
    padding: var(--card-padding);
    scroll-margin-top: 80px;
  }

  .settings-card.general-card {
    grid-column: span 2;
  }

  .settings-card-header {
    display: flex;
    align-items: flex-start;
    gap: clamp(8px, 1vw, 13px);
    margin-bottom: clamp(11px, 1.4vw, 20px);
  }

  .settings-card-icon {
    width: clamp(15px, 1.6vw, 22px);
    height: clamp(15px, 1.6vw, 22px);
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--accent);
    filter: drop-shadow(0 2px 4px rgba(76, 11, 250, .1));
  }

  .settings-card-title {
    margin: 0;
    color: #1e345a;
    font-size: var(--title-size);
    font-weight: 650;
    line-height: 1.35;
  }

  .settings-card-description {
    margin: 4px 0 0;
    color: var(--muted);
    font-size: var(--description-size);
    line-height: 1.4;
  }

  .general-card-content {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
    gap: clamp(18px, 2.2vw, 32px);
  }

  .settings-fields {
    display: flex;
    flex-direction: column;
    gap: clamp(7px, .8vw, 12px);
  }

  .settings-field {
    min-width: 0;
  }

  .settings-label {
    display: block;
    margin-bottom: clamp(3px, .35vw, 5px);
    color: #55719a;
    font-size: var(--label-size);
    font-weight: 500;
    line-height: 1.3;
  }

  .settings-control {
    display: block;
    width: 100%;
    min-width: 0;
    height: var(--field-height);
    padding: 0 clamp(6px, .65vw, 9px);
    border: 1px solid #e0e9f4;
    border-radius: 3px;
    outline: none;
    color: #35527b;
    background: #fbfdff;
    font-size: var(--body-size);
    line-height: normal;
    transition: border-color .15s, box-shadow .15s;
  }

  .settings-control:focus {
    border-color: #9974f8;
    box-shadow: 0 0 0 2px rgba(76, 11, 250, .07);
  }

  .settings-control::placeholder {
    color: #6480a5;
    opacity: 1;
  }

  .settings-select-wrapper,
  .settings-input-wrapper {
    position: relative;
  }

  .settings-select {
    appearance: none;
    padding-right: 24px;
  }

  .settings-control-icon {
    position: absolute;
    top: 50%;
    right: 7px;
    width: clamp(9px, 1vw, 13px);
    height: clamp(9px, 1vw, 13px);
    transform: translateY(-50%);
    color: #6486b2;
    pointer-events: none;
  }

  .settings-input-action {
    position: absolute;
    top: 50%;
    right: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border: 0;
    border-radius: 2px;
    background: transparent;
    color: #6486b2;
    transform: translateY(-50%);
    cursor: pointer;
  }

  .settings-input-action svg {
    width: clamp(10px, 1.1vw, 14px);
    height: clamp(10px, 1.1vw, 14px);
  }

  .system-information {
    padding-left: clamp(12px, 1.7vw, 24px);
    border-left: 1px solid #f0f4fa;
  }

  .system-information h3 {
    margin: 0 0 clamp(11px, 1.3vw, 19px);
    color: #243b61;
    font-size: var(--title-size);
    font-weight: 650;
  }

  .system-information dl {
    display: grid;
    grid-template-columns: minmax(0, .9fr) minmax(0, 1fr);
    align-items: center;
    column-gap: 8px;
    row-gap: clamp(12px, 1.45vw, 21px);
    margin: 0;
    font-size: var(--body-size);
  }

  .system-information dt {
    color: #5a769e;
  }

  .system-information dd {
    min-width: 0;
    margin: 0;
    color: #35527b;
    overflow-wrap: anywhere;
  }

  .production-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 4px;
    color: #26986c;
    background: #ddf6e8;
    font-size: var(--description-size);
  }

  .network-tabs {
    display: flex;
    gap: clamp(15px, 2vw, 28px);
    margin-bottom: clamp(10px, 1.2vw, 17px);
    border-bottom: 1px solid #e9eef7;
  }

  .network-tab {
    min-width: 58px;
    padding: 0 10px 8px;
    border: 0;
    border-bottom: 2px solid transparent;
    color: #6782a6;
    background: transparent;
    font-size: var(--body-size);
    cursor: pointer;
  }

  .network-tab.active {
    border-bottom-color: var(--accent);
    color: var(--accent);
  }

  .settings-inline-field {
    display: grid;
    grid-template-columns: minmax(0, .9fr) minmax(0, 1.6fr);
    gap: clamp(6px, .8vw, 12px);
    align-items: center;
  }

  .settings-inline-field .settings-label {
    margin-bottom: 0;
  }

  .connection-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 7px;
    margin-top: clamp(11px, 1.45vw, 21px);
    padding-top: clamp(6px, .7vw, 10px);
    border-top: 1px solid #eff4fa;
  }

  .connection-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #209a6f;
    font-size: var(--body-size);
    font-weight: 500;
  }

  .connection-status.neutral {
    color: #7b8ea8;
  }

  .connection-status.error {
    color: #c64949;
  }

  .connection-dot {
    width: clamp(5px, .65vw, 8px);
    height: clamp(5px, .65vw, 8px);
    flex-shrink: 0;
    border-radius: 50%;
    background: currentColor;
  }

  .connection-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    height: clamp(21px, 2.35vw, 32px);
    padding: 0 clamp(6px, .8vw, 12px);
    border: 1px solid #dce6f4;
    border-radius: 4px;
    color: #3464a5;
    background: #fff;
    font-size: var(--body-size);
    white-space: nowrap;
    cursor: pointer;
  }

  .connection-button:hover {
    background: #f5f8ff;
  }

  .connection-button:disabled {
    opacity: .6;
    cursor: not-allowed;
  }

  .connection-button svg {
    width: clamp(10px, 1vw, 14px);
    height: clamp(10px, 1vw, 14px);
  }

  .settings-toggle-list {
    display: flex;
    flex-direction: column;
    gap: clamp(14px, 1.8vw, 26px);
  }

  .settings-toggle-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  .settings-toggle-title {
    margin: 0;
    color: #36527a;
    font-size: var(--body-size);
    font-weight: 500;
    line-height: 1.4;
  }

  .settings-toggle-description {
    margin: 2px 0 0;
    color: #718aab;
    font-size: var(--description-size);
    line-height: 1.4;
  }

  .settings-switch {
    position: relative;
    display: inline-flex;
    width: clamp(18px, 2vw, 28px);
    height: clamp(11px, 1.2vw, 16px);
    flex-shrink: 0;
    margin-top: 1px;
    cursor: pointer;
  }

  .settings-switch input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }

  .settings-switch-track {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: #bfccdf;
    transition: background .2s;
  }

  .settings-switch-track::after {
    position: absolute;
    top: 2px;
    left: 2px;
    width: calc(clamp(11px, 1.2vw, 16px) - 4px);
    height: calc(clamp(11px, 1.2vw, 16px) - 4px);
    border-radius: 50%;
    background: white;
    content: '';
    box-shadow: 0 1px 2px rgba(0, 0, 0, .1);
    transition: left .2s;
  }

  .settings-switch input:checked + .settings-switch-track {
    background: var(--accent);
  }

  .settings-switch input:checked + .settings-switch-track::after {
    left: calc(
      100% - clamp(11px, 1.2vw, 16px) + 2px
    );
  }

  .settings-switch input:focus-visible + .settings-switch-track {
    outline: 2px solid #a48aff;
    outline-offset: 3px;
  }

  .security-policy {
    margin-top: clamp(15px, 2vw, 28px);
  }

  .notification-fields {
    margin-top: clamp(18px, 2.4vw, 34px);
  }

  .compact-card .settings-card-header {
    margin-bottom: clamp(10px, 1vw, 15px);
  }

  .compact-card .settings-fields {
    gap: clamp(7px, .8vw, 12px);
  }

  .settings-save-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: -10px;
    padding-right: 3px;
    position: relative;
    z-index: 1;
  }

  .settings-save-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: clamp(23px, 2.6vw, 36px);
    padding: 0 clamp(11px, 1.3vw, 19px);
    border: 0;
    border-radius: 4px;
    background: var(--accent);
    color: white;
    font-size: var(--body-size);
    font-weight: 500;
    box-shadow: 0 3px 8px rgba(76, 11, 250, .12);
    cursor: pointer;
  }

  .settings-save-button:hover {
    background: #3e08d1;
  }

  .settings-save-button:disabled {
    opacity: .65;
    cursor: not-allowed;
  }

  .settings-save-button svg {
    width: clamp(10px, 1.1vw, 15px);
    height: clamp(10px, 1.1vw, 15px);
  }

  .settings-message {
    color: #55719a;
    font-size: var(--body-size);
  }

  @media (max-width: 767px) {
    .blockchain-settings {
      --field-height: 34px;
      --label-size: 11px;
      --body-size: 12px;
      --title-size: 14px;
      --description-size: 11px;
      --card-padding: 16px;
      --gap: 12px;
    }

    .settings-layout {
      grid-template-columns: 1fr;
    }

    .settings-sidebar {
      display: flex;
      gap: 6px;
      overflow-x: auto;
      padding: 8px;
    }

    .settings-nav-button {
      width: auto;
      flex-shrink: 0;
      min-height: 34px;
      margin: 0;
      padding: 8px 12px;
      white-space: nowrap;
    }

    .settings-nav-icon {
      width: 15px;
      height: 15px;
    }

    .settings-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-rows: none;
    }

    .settings-card.general-card {
      grid-column: 1 / -1;
    }

    .settings-card-header {
      margin-bottom: 18px;
    }

    .settings-card-icon {
      width: 19px;
      height: 19px;
    }

    .settings-toggle-list {
      gap: 18px;
    }

    .settings-switch {
      width: 28px;
      height: 16px;
    }

    .settings-switch-track::after {
      width: 12px;
      height: 12px;
    }

    .settings-switch input:checked + .settings-switch-track::after {
      left: 14px;
    }

    .settings-inline-field {
      grid-template-columns: 1fr;
      gap: 4px;
    }

    .settings-save-footer {
      margin-top: 12px;
      padding-right: 0;
    }

    .settings-save-button {
      height: 36px;
      font-size: 12px;
    }
  }

  @media (max-width: 480px) {
    .settings-grid {
      grid-template-columns: 1fr;
    }

    .general-card-content {
      grid-template-columns: 1fr;
      gap: 22px;
    }

    .system-information {
      padding: 18px 0 0;
      border-left: 0;
      border-top: 1px solid #edf2f8;
    }

    .system-information dl {
      row-gap: 14px;
    }

    .settings-inline-field {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1.5fr);
    }

    .settings-save-footer {
      flex-wrap: wrap;
    }
  }
`;

function Card({ id, icon: Icon, title, description, className = '', children }) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-heading`}
      className={`settings-card ${className}`}
    >
      <div className="settings-card-header">
        <Icon className="settings-card-icon" aria-hidden="true" />

        <div>
          <h2 id={`${id}-heading`} className="settings-card-title">
            {title}
          </h2>

          <p className="settings-card-description">{description}</p>
        </div>
      </div>

      {children}
    </section>
  );
}

function SelectControl({ id, name, value, onChange, options }) {
  return (
    <div className="settings-select-wrapper">
      <select
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        className="settings-control settings-select"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <ChevronDown className="settings-control-icon" aria-hidden="true" />
    </div>
  );
}

function InputControl({
  id,
  name,
  label,
  value,
  onChange,
  password,
  trailingIcon,
}) {
  const [visible, setVisible] = useState(false);
  const hasAction = password || trailingIcon;

  return (
    <div className="settings-input-wrapper">
      <input
        id={id}
        name={name}
        type={password && !visible ? 'password' : 'text'}
        value={value}
        onChange={onChange}
        placeholder={password ? '••••••••••••••••••••••••' : undefined}
        autoComplete={password ? 'new-password' : undefined}
        className="settings-control"
        style={hasAction ? { paddingRight: 26 } : undefined}
      />

      {password && (
        <button
          type="button"
          className="settings-input-action"
          onClick={() => setVisible((previous) => !previous)}
          aria-label={`${visible ? 'Hide' : 'Show'} ${label}`}
        >
          {visible ? <EyeOff /> : <Eye />}
        </button>
      )}

      {trailingIcon && !password && (
        <ExternalLink className="settings-control-icon" aria-hidden="true" />
      )}
    </div>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  options,
  inline = false,
  password = false,
  trailingIcon = false,
}) {
  const id = `blockchain-settings-${name}`;
  const fieldClass = inline
    ? 'settings-field settings-inline-field'
    : 'settings-field';

  return (
    <div className={fieldClass}>
      <label htmlFor={id} className="settings-label">
        {label}
      </label>

      {options ? (
        <SelectControl
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          options={options}
        />
      ) : (
        <InputControl
          id={id}
          name={name}
          label={label}
          value={value}
          onChange={onChange}
          password={password}
          trailingIcon={trailingIcon}
        />
      )}
    </div>
  );
}

function Switch({ label, checked, onChange }) {
  return (
    <label className="settings-switch">
      <input
        type="checkbox"
        role="switch"
        aria-label={label}
        checked={checked}
        onChange={onChange}
      />

      <span className="settings-switch-track" aria-hidden="true" />
    </label>
  );
}

function ToggleRow({ title, description, checked, onChange }) {
  return (
    <div className="settings-toggle-row">
      <div>
        <p className="settings-toggle-title">{title}</p>

        {description && (
          <p className="settings-toggle-description">{description}</p>
        )}
      </div>

      <Switch label={title} checked={checked} onChange={onChange} />
    </div>
  );
}

function ConnectionFooter({ status, ipfs = false, onTest, disabled }) {
  const connectedText = ipfs ? 'Connected to IPFS' : 'Connected';
  const statusMap = {
    testing: { text: 'Testing…', className: 'neutral' },
    connected: { text: connectedText, className: '' },
    error: { text: 'Connection failed', className: 'error' },
  };
  const { text, className: statusClass } = statusMap[status] ?? {
    text: 'Not tested',
    className: 'neutral',
  };

  return (
    <div className="connection-footer">
      <span aria-live="polite" className={`connection-status ${statusClass}`}>
        <span className="connection-dot" />
        {text}
      </span>

      <button
        type="button"
        className="connection-button"
        onClick={onTest}
        disabled={disabled || status === 'testing'}
        title={disabled ? 'Connect a connection-test handler to enable this' : ''}
      >
        <Link2 aria-hidden="true" />
        {ipfs ? 'Test IPFS' : 'Test Connection'}
      </button>
    </div>
  );
}

export default function BlockchainSettings({
  initialValues = {},
  onSave,
  onTestConnection,
  onTestIPFS,
  initialNetworkStatus = 'idle',
  initialIPFSStatus = 'idle',
}) {
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const activeTab = 'settings';

  const [activeSubSection, setActiveSubSection] = useState('General');
  const [networkTab, setNetworkTab] = useState('blockchain');

  const [formData, setFormData] = useState(() => ({
    ...INITIAL_FORM_DATA,
    ...initialValues,
  }));

  const [networkStatus, setNetworkStatus] = useState(initialNetworkStatus);
  const [ipfsStatus, setIPFSStatus] = useState(initialIPFSStatus);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const handleToggle = (key) => {
    setFormData((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
    setMessage('');
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    setMessage('');

    if (
      ['networkType', 'rpcEndpoint', 'chainId', 'blockExplorer'].includes(name)
    ) {
      setNetworkStatus('idle');
    }

    if (
      ['ipfsNodeType', 'gatewayUrl', 'pinningService', 'apiKey', 'secretKey']
        .includes(name)
    ) {
      setIPFSStatus('idle');
    }
  };

  const handleSectionClick = (item) => {
    setActiveSubSection(item.name);

    const section = contentRef.current?.querySelector(
      `#settings-${item.section}`
    );

    section?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  };

  const handleTest = async (isIPFS) => {
    const callback = isIPFS ? onTestIPFS : onTestConnection;
    const setStatus = isIPFS ? setIPFSStatus : setNetworkStatus;

    if (!callback) return;

    setStatus('testing');

    try {
      // The callback must return true only after a successful real test.
      const success = await callback({ ...formData });
      setStatus(success === true ? 'connected' : 'error');
    } catch {
      setStatus('error');
    }
  };

  const handleSave = async () => {
    if (!onSave) {
      setMessage('Connect onSave to your API to persist these settings.');
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      await onSave({ ...formData });
      setMessage('Settings saved successfully.');
    } catch {
      setMessage('Unable to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const field = (name, label, options, extra = {}) => (
    <Field
      name={name}
      label={label}
      value={formData[name]}
      onChange={handleChange}
      options={options}
      {...extra}
    />
  );

  return (
    <div className="blockchain-settings">
      <style>{styles}</style>

      {/* Top tabs: kept unchanged */}
      <header className="sticky top-0 z-40 bg-white border-b border-[#e5edf7]">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between px-0">
          <nav className="flex gap-6 overflow-x-auto">
            {tabs.map((tab) => {
              const tabKey = tab.toLowerCase().replace(/\s+/g, '-');
              const isActive = activeTab === tabKey;

              return (
                <button
                  type="button"
                  key={tab}
                  onClick={() => navigate(TAB_ROUTES[tabKey])}
                  className={`whitespace-nowrap pb-3 pt-1 text-xs font-medium transition ${
                    isActive
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-900 hover:text-gray-900'
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="settings-shell">
        <div className="settings-layout">
          {/* Full-height left sidebar */}
          <aside className="settings-sidebar" aria-label="Settings sections">
            {settingsNavItems.map((item) => {
              const Icon = item.icon;
              const active = activeSubSection === item.name;

              return (
                <button
                  type="button"
                  key={item.name}
                  onClick={() => handleSectionClick(item)}
                  aria-current={active ? 'location' : undefined}
                  className={`settings-nav-button ${active ? 'active' : ''}`}
                >
                  <Icon className="settings-nav-icon" aria-hidden="true" />
                  <span>{item.name}</span>
                </button>
              );
            })}
          </aside>

          <div className="settings-content" ref={contentRef}>
            <div className="settings-grid">
              {/* Row 1: General spans two columns */}
              <Card
                id="settings-general"
                icon={SettingsIcon}
                title="General Settings"
                description="Configure general application settings and preferences."
                className="general-card"
              >
                <div className="general-card-content">
                  <div className="settings-fields">
                    {field('orgName', 'Organization Name')}
                    {field('appName', 'Application Name')}

                    {field('timezone', 'Timezone', [
                      'Asia/Kolkata (UTC+05:30)',
                      'UTC',
                      'America/New_York (UTC-05:00)',
                    ])}

                    {field('defaultLanguage', 'Default Language', [
                      'English',
                      'Spanish',
                      'French',
                    ])}
                  </div>

                  <div className="system-information">
                    <h3>System Information</h3>

                    <dl>
                      <dt>Version</dt>
                      <dd>v1.2.0</dd>

                      <dt>Build</dt>
                      <dd>2025-05-28.1</dd>

                      <dt>Environment</dt>
                      <dd>
                        <span className="production-badge">Production</span>
                      </dd>

                      <dt>Uptime</dt>
                      <dd>12 days, 4 hours</dd>

                      <dt>Support Email</dt>
                      <dd>support@intrustlabs.com</dd>
                    </dl>
                  </div>
                </div>
              </Card>

              {/* Row 1: Network */}
              <Card
                id="settings-network"
                icon={Share2}
                title="Network Configuration"
                description="Configure blockchain and IPFS network endpoints."
              >
                <div className="network-tabs">
                  <button
                    type="button"
                    aria-pressed={networkTab === 'blockchain'}
                    onClick={() => setNetworkTab('blockchain')}
                    className={`network-tab ${
                      networkTab === 'blockchain' ? 'active' : ''
                    }`}
                  >
                    Blockchain
                  </button>

                  <button
                    type="button"
                    aria-pressed={networkTab === 'ipfs'}
                    onClick={() => setNetworkTab('ipfs')}
                    className={`network-tab ${
                      networkTab === 'ipfs' ? 'active' : ''
                    }`}
                  >
                    IPFS
                  </button>
                </div>

                {networkTab === 'blockchain' ? (
                  <>
                    <div className="settings-fields">
                      {field(
                        'networkType',
                        'Network Type',
                        [
                          'Ethereum (Mainnet)',
                          'Polygon (PoS)',
                          'Binance Smart Chain',
                        ],
                        { inline: true }
                      )}

                      {field('rpcEndpoint', 'RPC Endpoint', undefined, {
                        inline: true,
                      })}

                      {field('chainId', 'Chain ID', undefined, {
                        inline: true,
                      })}

                      {field('blockExplorer', 'Block Explorer URL', undefined, {
                        inline: true,
                      })}
                    </div>

                    <ConnectionFooter
                      status={networkStatus}
                      onTest={() => handleTest(false)}
                      disabled={!onTestConnection}
                    />
                  </>
                ) : (
                  <>
                    <div className="settings-fields">
                      {field(
                        'ipfsNodeType',
                        'Node Type',
                        ['Public Gateway', 'Dedicated Node', 'Local Daemon'],
                        { inline: true }
                      )}

                      {field('gatewayUrl', 'Gateway URL', undefined, {
                        inline: true,
                      })}

                      {field(
                        'pinningService',
                        'Pinning Service',
                        ['Pinata', 'Infura IPFS', 'Local Node'],
                        { inline: true }
                      )}
                    </div>

                    <ConnectionFooter
                      ipfs
                      status={ipfsStatus}
                      onTest={() => handleTest(true)}
                      disabled={!onTestIPFS}
                    />
                  </>
                )}
              </Card>

              {/* Row 2: IPFS */}
              <Card
                id="settings-ipfs"
                icon={Box}
                title="IPFS Configuration"
                description="Configure IPFS node and storage settings."
              >
                <div className="settings-fields">
                  {field('ipfsNodeType', 'IPFS Node Type', [
                    'Public Gateway',
                    'Dedicated Node',
                    'Local Daemon',
                  ])}

                  {field('gatewayUrl', 'Gateway URL')}

                  {field('pinningService', 'Pinning Service', [
                    'Pinata',
                    'Infura IPFS',
                    'Local Node',
                  ])}

                  {field('apiKey', 'API Key', undefined, {
                    password: true,
                  })}

                  {field('secretKey', 'Secret Key', undefined, {
                    password: true,
                  })}
                </div>

                <ConnectionFooter
                  ipfs
                  status={ipfsStatus}
                  onTest={() => handleTest(true)}
                  disabled={!onTestIPFS}
                />
              </Card>

              {/* Row 2: Security */}
              <Card
                id="settings-security"
                icon={ShieldCheck}
                title="Security Settings"
                description="Manage security and encryption settings."
              >
                <div className="settings-toggle-list">
                  <ToggleRow
                    title="Enable End-to-End Encryption"
                    description="Encrypt data before storing on blockchain/IPFS"
                    checked={formData.e2eEncryption}
                    onChange={() => handleToggle('e2eEncryption')}
                  />

                  <ToggleRow
                    title="Use Hardware Security Module (HSM)"
                    description="Secure key management with HSM"
                    checked={formData.hsm}
                    onChange={() => handleToggle('hsm')}
                  />

                  <ToggleRow
                    title="Enable Multi-Signature"
                    description="Require multiple approvals for critical actions"
                    checked={formData.multiSig}
                    onChange={() => handleToggle('multiSig')}
                  />

                  <ToggleRow
                    title="Enable Certificate Pinning"
                    description="Validate certificates for external services"
                    checked={formData.certPinning}
                    onChange={() => handleToggle('certPinning')}
                  />
                </div>

                <div className="security-policy">
                  {field('keyRotation', 'Key Rotation Policy', [
                    'Every 30 days',
                    'Every 90 days',
                    'Every 180 days',
                    'Manual',
                  ])}
                </div>
              </Card>

              {/* Row 2: Notifications */}
              <Card
                id="settings-notifications"
                icon={Bell}
                title="Notifications"
                description="Configure alerts and notifications."
              >
                <div className="settings-toggle-list">
                  <ToggleRow
                    title="Email Notifications"
                    description="Receive system and transaction alerts"
                    checked={formData.emailNotifications}
                    onChange={() => handleToggle('emailNotifications')}
                  />

                  <ToggleRow
                    title="SMS Notifications"
                    description="Receive critical alerts via SMS"
                    checked={formData.smsNotifications}
                    onChange={() => handleToggle('smsNotifications')}
                  />
                </div>

                <div className="settings-fields notification-fields">
                  {field('webhookUrl', 'Webhook URL', undefined, {
                    trailingIcon: true,
                  })}

                  {field('notificationEvents', 'Notification Events', [
                    'All Events',
                    'Critical Only',
                    'Errors & Warnings',
                  ])}
                </div>
              </Card>

              {/* Row 3: Audit */}
              <Card
                id="settings-audit"
                icon={FileText}
                title="Audit & Logging"
                description="Configure audit trail and log retention settings."
                className="compact-card"
              >
                <div className="settings-fields">
                  {field('logLevel', 'Log Level', [
                    'Info',
                    'Debug',
                    'Warn',
                    'Error',
                  ])}

                  {field('retentionPeriod', 'Retention Period', [
                    '12 Months',
                    '6 Months',
                    '24 Months',
                  ])}
                </div>
              </Card>

              {/* Row 3: Integration */}
              <Card
                id="settings-integration"
                icon={Link2}
                title="Integration Settings"
                description="Configure third-party integrations."
                className="compact-card"
              >
                <div className="settings-fields">
                  {field(
                    'siemIntegration',
                    'SIEM Integration',
                    ['Splunk', 'Datadog', 'Elasticsearch'],
                    { inline: true }
                  )}

                  {field(
                    'monitoring',
                    'Monitoring',
                    ['Prometheus', 'Grafana', 'New Relic'],
                    { inline: true }
                  )}

                  <ToggleRow
                    title="API Access"
                    checked={formData.apiAccess}
                    onChange={() => handleToggle('apiAccess')}
                  />
                </div>
              </Card>

              {/* Row 3: Backup */}
              <Card
                id="settings-backup"
                icon={Cloud}
                title="Backup & Recovery"
                description="Configure backup and disaster recovery options."
                className="compact-card"
              >
                <div className="settings-fields">
                  <ToggleRow
                    title="Auto Backup"
                    checked={formData.autoBackup}
                    onChange={() => handleToggle('autoBackup')}
                  />

                  {field(
                    'backupFrequency',
                    'Backup Frequency',
                    ['Daily', 'Weekly', 'Hourly'],
                    { inline: true }
                  )}

                  {field(
                    'rpo',
                    'Recovery Point Objective (RPO)',
                    ['1 Hour', '4 Hours', '24 Hours'],
                    { inline: true }
                  )}
                </div>
              </Card>
            </div>

            <div className="settings-save-footer">
              {message && (
                <span className="settings-message" role="status">
                  {message}
                </span>
              )}

              <button
                type="button"
                className="settings-save-button"
                onClick={handleSave}
                disabled={saving}
              >
                <Save aria-hidden="true" />
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}